import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types
interface Article {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: 'spare_part' | 'consumable' | 'tool' | 'safety_equipment' | 'other';
  status: 'active' | 'discontinued' | 'obsolete';
  unit: string;
  current_stock: number;
  reserved_stock: number;
  available_stock: number;
  min_stock: number;
  reorder_point: number;
  unit_cost: number;
  average_cost: number;
  manufacturer?: string;
  created_at: string;
  updated_at: string;
}

interface ArticleListParams {
  category?: string;
  equipmentCategoryId?: string;
  status?: string;
  supplierId?: string;
  lowStock?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

interface ArticleListResponse {
  items: Article[];
  total: number;
}

interface ArticleStats {
  total: number;
  byCategory: Record<string, number>;
  byStatus: Record<string, number>;
  lowStockCount: number;
  totalValue: number;
}

// Query keys
export const articleKeys = {
  all: ['articles'] as const,
  lists: () => [...articleKeys.all, 'list'] as const,
  list: (filters: ArticleListParams) => [...articleKeys.lists(), filters] as const,
  details: () => [...articleKeys.all, 'detail'] as const,
  detail: (id: string) => [...articleKeys.details(), id] as const,
  stats: () => [...articleKeys.all, 'stats'] as const,
};

// Hooks
export function useArticleList(params: ArticleListParams = {}) {
  return useQuery({
    queryKey: articleKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.category) searchParams.set('category', params.category);
      if (params.equipmentCategoryId) searchParams.set('equipment_category_id', params.equipmentCategoryId);
      if (params.status) searchParams.set('status', params.status);
      if (params.supplierId) searchParams.set('supplier_id', params.supplierId);
      if (params.lowStock) searchParams.set('low_stock', 'true');
      if (params.search) searchParams.set('search', params.search);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<ArticleListResponse>(
        `/articles?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useArticleDetail(id: string) {
  return useQuery({
    queryKey: articleKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ article: Article }>(`/articles/${id}`);
      return response.data.article;
    },
    enabled: !!id,
  });
}

export function useArticleStats() {
  return useQuery({
    queryKey: articleKeys.stats(),
    queryFn: async () => {
      const response = await apiClient.get<ArticleStats>('/articles/stats');
      return response.data;
    },
  });
}

export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Article>) => {
      const response = await apiClient.post<{ article: Article }>('/articles', data);
      return response.data.article;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.stats() });
    },
  });
}

export function useUpdateArticle(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Article>) => {
      const response = await apiClient.put<{ article: Article }>(`/articles/${id}`, data);
      return response.data.article;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: articleKeys.stats() });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/articles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: articleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: articleKeys.stats() });
    },
  });
}
